<script setup lang="ts">
import VideoHeader from "../../../assets/image/eventUs/eventUs-student-header.mp4";
import IconEventusStudent from "../../../assets/image/eventUs/icons/icon-EventUs-association.svg";
import EventusStudentTitle from "../../../assets/image/eventUs/eventUs-student-title.svg";
import IconEventusStudentTeam from "../../../assets/image/eventUs/icons/icon-eventUs-student-team.svg";
import IconMedals from "../../../assets/image/eventUs/icons/all-medals.svg";
import IconCamera from "../../../assets/image/eventUs/icons/option-camera.svg";
import IconTrack from "../../../assets/image/eventUs/icons/option-race.svg";
import EventUsNavigation from "../../../components/website/EventUsNavigation.vue";
import ImageHeader from "../../../components/website/ImageHeader.vue";
import Who from "../../../components/website/LayoutForWho.vue";
import { useTranslate } from "addeus-common-library/stores/translate";
import { useOwner } from "../../../stores/website/owner";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { computed } from "vue";
import { storeToRefs } from "pinia";
import PackCard from "../../../components/website/PackCard.vue";
import type { OptionCardProps } from "../../../components/website/OptionCard.vue";
import OptionCard from "../../../components/website/OptionCard.vue";
import SmallCard from "../../../components/website/SmallCard.vue";
import VFlex from "addeus-common-library/components/base/flex/VFlex.vue";
import Translate from "addeus-common-library/components/base/translation/Translate.vue";
import Banner from "../../../components/website/Banner.vue";
import { useSeoMeta } from "../../../composables/useSeoMeta";

const breakpoints = useBreakpoints(breakpointsTailwind);
const isSmallerThanXl = breakpoints.smallerOrEqual("xl");

const { setTranslateNamespace, translate } = useTranslate();
setTranslateNamespace(".eventUs.student");

useSeoMeta({
    title: "Soirée étudiante karting | Student Karty - BDE et BDS",
    description:
        "Soirée étudiante karting électrique Student Karty : courses par équipes, challenges décalés, piste privatisée. Formule spéciale BDE, BDS et associations étudiantes.",
    path: "/event-us/student",
    breadcrumbs: [
        { name: "Accueil", path: "/" },
        { name: "Événements chez nous", path: "/event-us" },
        { name: "Étudiants", path: "/event-us/student" },
    ],
    schemaOrg: [
        {
            "@context": "https://schema.org",
            "@type": "Event",
            name: "Student Karty - Soirées étudiantes karting",
            description:
                "Soirées étudiantes karting avec courses par équipes, challenges décalés et piste privatisée.",
            url: "https://speed2max.com/event-us/student",
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            organizer: {
                "@type": "Organization",
                name: "Speed2Max",
                url: "https://speed2max.com",
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
                {
                    "@type": "Question",
                    name: "Comment fonctionne la formule Student Karty ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Le Student Karty se déroule par équipes de 2 à 4 personnes avec piste privatisée, courses par équipes, challenges décalés, podium et consommation incluse.",
                    },
                },
                {
                    "@type": "Question",
                    name: "La formule Student Karty est-elle réservée aux BDE ?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "La formule est ouverte à tous les groupes étudiants : BDE, BDS, associations étudiantes ou simplement groupes d'amis étudiants.",
                    },
                },
            ],
        },
    ],
});

const ownerManager = useOwner();
const { owner } = storeToRefs(useOwner());

function removePrefixPhoneNumber(phoneNumber: string) {
    return phoneNumber.replace(/^\+[0-9]+\s*/, "0");
}

const optionPriceAttributeName = "priceStudentKartOption";
const options = computed(() => {
    const array: OptionCardProps[] = [];
    for (let i = 1; i <= 2; i++) {
        const website = owner.value?.website;
        const data = {
            name: translate(`speed2max.eventUs.student.options.option${i}.name`).value,
            title: translate(`speed2max.eventUs.student.options.option${i}.title`).value,
            content: translate(`speed2max.eventUs.student.options.option${i}.content`)
                .value,
            price: website ? website[`${optionPriceAttributeName}${i}`] : 0,
        };
        array.push(data);
    }
    return array;
});

function getCardClass(index: number) {
    const css: string[] = [`option-card-${index + 1}`];
    if (isSmallerThanXl.value) {
        css.push("my-3");
    } else {
        css.push("my-auto");
    }
    return css;
}
const icons: string[] = [IconCamera, IconTrack];
</script>

<template>
    <VFlex flex-direction="column" class="has-background-dark">
        <EventUsNavigation current="student"></EventUsNavigation>
        <ImageHeader :hide-background="true" secondary>
            <template #image>
                <div :class="{ 'video-landing': !isSmallerThanXl }" style="display: flex">
                    <video
                        playsinline
                        loop
                        muted
                        autoplay
                        crossorigin="anonymous"
                        style="height: 50vh"
                        :class="{ 'mb-4': isSmallerThanXl }">
                        <source :src="VideoHeader" />
                    </video>
                </div>
            </template>

            <template #title>
                <span> <Translate path=".title"></Translate></span>
            </template>
            <template #subtitle>
                <span class="has-text-white">
                    <Translate path=".subtitle"></Translate>&nbsp;
                    <span class="has-text-primary">
                        <Translate path=".subtitle2"></Translate>&nbsp;
                    </span>
                    <Translate path=".subtitle3"></Translate>
                </span>
            </template>
        </ImageHeader>

        <Who>
            <template #title>
                <Translate path=".title"></Translate>
            </template>
            <template #content>
                <div class="mb-2">
                    <Translate path=".content"></Translate>
                </div>
                <div style="white-space: pre-line">
                    <Translate path=".content2"></Translate>
                </div>
            </template>
            <template #icon>
                <img :src="IconEventusStudent" alt="Icône soirée étudiante karting" />
            </template>
            <template #titleP1>
                <Translate path=".titleP1"></Translate>
            </template>
            <template #titleP2>
                <Translate path=".titleP2"></Translate>
            </template>
        </Who>

        <div class="pack-grid" :class="{ 'mr-6': !isSmallerThanXl }">
            <PackCard
                class="pack-card"
                color="primary"
                :head-image="EventusStudentTitle"
                :price="owner?.website?.priceStudentKart">
                <template #content>
                    <TranslateNamespace path=".pack">
                        <VFlex
                            flex-direction="column"
                            class="is-flex is-align-self-stretch py-3 is-size-content"
                            align-items="start">
                            <VFlex flex-direction="column">
                                <VFlex
                                    flex-direction="row"
                                    align-items="center"
                                    class="has-text-white is-uppercase my-2 is-weight-600">
                                    <img
                                        :src="IconEventusStudentTeam"
                                        alt="Icône équipe Student Karty"
                                        class="mr-3"
                                        style="height: 30px" />
                                    <span>
                                        <Translate path=".group" /> &nbsp;
                                        <span class="has-text-primary">
                                            <Translate
                                                path=".groupSize"
                                                :values="{ min: 2, max: 4 }" />
                                        </span>
                                    </span>
                                </VFlex>
                                <span class="is-italic has-text-weight-light">
                                    <Translate path=".groupDetail" />
                                </span>
                            </VFlex>
                            <ul class="pack-content ml-4">
                                <li>
                                    <p
                                        class="has-text-primary is-uppercase my-2 mx-4 is-weight-600">
                                        <Translate path=".exclu" />
                                    </p>
                                </li>
                                <li>
                                    <p
                                        class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                        <Translate path=".drink"></Translate>
                                    </p>
                                </li>
                                <li>
                                    <p
                                        class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                        <Translate path=".podium"></Translate>
                                        <span>
                                            <img
                                                :src="IconMedals"
                                                style="height: 2rem"
                                                class="mx-2 my-auto"
                                                alt="medal" />
                                        </span>
                                    </p>
                                </li>
                                <li>
                                    <p
                                        class="has-text-white is-uppercase my-2 mx-4 is-weight-600">
                                        <Translate path=".fun"></Translate>
                                    </p>
                                </li>
                            </ul>
                        </VFlex>
                    </TranslateNamespace>
                </template>
            </PackCard>
            <OptionCard
                v-for="(option, index) in options"
                :key="option.name"
                :class="getCardClass(index)"
                :name="option.name"
                :title="option.title"
                :title-icon="icons[index]"
                :content="option.content"
                :price="option.price"></OptionCard>
        </div>
        <div class="mx-6 no-margin-mobile">
            <h3
                class="mx-6 mb-5 mt-3 is-size-5 is-underlined has-text-primary has-text-weight-bold is-family-code">
                <Translate>.pack.planTitle</Translate>
            </h3>
            <ul class="plan-style mx-6">
                <li v-for="index in 7" :key="index" class="ml-5 mb-2 is-size-5">
                    <p class="ml-3 has-text-weight-bold has-text-white">
                        <Translate :path="`.pack.plan${index}`" /> &nbsp;
                        <span
                            v-if="[1, 2, 4, 6].includes(index)"
                            class="has-text-primary">
                            <Translate :path="`.pack.plan${index}Bis`" />
                        </span>
                    </p>
                </li>
            </ul>
        </div>
        <div class="mx-6 no-margin-mobile">
            <h3
                class="mt-6 mx-6 mb-5 is-size-5 is-underlined has-text-primary has-text-weight-bold is-family-code">
                <Translate>.pack.challengeTitle</Translate>
            </h3>
            <div class="block-grid challenge-grid mx-6">
                <SmallCard
                    v-for="index in 4"
                    :key="index"
                    style="min-height: 50px"
                    :content="
                        translate(`speed2max.eventUs.student.pack.challenge${index}`)
                            .value
                    "></SmallCard>
            </div>
        </div>

        <SmallCard
            :with-border="false"
            class="mt-6"
            :class="{ 'mx-auto': !isSmallerThanXl, 'mx-6': isSmallerThanXl }">
            <div>
                <p class="has-text-primary has-text-weight-bold is-size-5 is-capitalized">
                    <Translate path=".pack.infoTitle" />
                </p>
                <p class="has-text-white is-size-6">
                    <Translate path=".pack.infoSub" />
                </p>
            </div>
        </SmallCard>

        <VFlex class="px-6 py-5 mb-5" flex-direction="column">
            <VFlex v-if="ownerManager.ownerID === null" :flex-direction="'column'">
                <p class="is-size-6 mb-4">
                    <Translate path=".infoChooseOwner"></Translate>
                </p>
                <ButtonPrimary
                    color="primary"
                    class="is-align-self-flex-start mt-none is-mohol is-size-6 mb-4"
                    style="font-family: mohol !important"
                    @click="$router.push('/owners')">
                    <!-- <VIcon icon="/images/kart/book-button-1.png"></VIcon> -->
                    <span class="is-uppercase">
                        <Translate path=".selectOwner"></Translate>
                    </span>
                </ButtonPrimary>
            </VFlex>
            <Banner class="m-5 has-gradient-background-dark-ltr w-100 is-weight-600">
                <Translate path=".banner"></Translate><br />
                <a
                    v-if="ownerManager.owner"
                    class="is-weight-900"
                    :href="`tel:${ownerManager.owner?.phoneNumber}`"
                    >{{ removePrefixPhoneNumber(ownerManager.owner?.phoneNumber) }}</a
                >
            </Banner>
        </VFlex>
        <Footer></Footer>
    </VFlex>
</template>

<style scoped lang="scss">
@use "sass:color";

.video-landing {
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
}

.pack-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr);

    .pack-card {
        grid-column-start: 1;
        grid-column-end: 3;
        grid-row-start: 1;
        grid-row-end: 3;
    }
}

@media screen and (max-width: 1360px) {
    .pack-grid {
        display: grid;
        grid-template-columns: 0.5fr 1fr 0.5fr;
        grid-template-rows: repeat(2, 1fr) repeat(2, 0.6fr);

        .pack-card {
            grid-column-start: 1;
            grid-column-end: 4;
            grid-row-start: 1;
            grid-row-end: 3;
        }

        .option-card-1 {
            margin-left: 3em;
            margin-right: 3em;
            grid-column-start: 1;
            grid-column-end: 4;
            grid-row-start: 3;
            grid-row-end: 4;
        }

        .option-card-2 {
            margin-left: 3em;
            margin-right: 3em;
            grid-column-start: 1;
            grid-column-end: 4;
            grid-row-start: 4;
            grid-row-end: 5;
        }
    }
}

ul.plan-style {
    list-style: decimal;
}

ul.pack-content {
    list-style: disc;
}

.option-card-1 {
    margin-left: 0.75em;
    margin-right: 0.75em;
}

.option-card-2 {
    margin-left: 0.75em;
    margin-right: 0.75em;
}

.w-100 {
    width: 100%;
}

.block-grid {
    display: grid !important;
}

.challenge-grid {
    grid-template-columns: repeat(2, 1fr);
    column-gap: 1rem;
    row-gap: 1rem;
}

@media screen and (max-width: 1360px) and (min-width: 501px) {
    .challenge-grid {
        grid-template-columns: repeat(2, 1fr) !important;
    }
}

@media (max-width: 500px) {
    .challenge-grid {
        grid-template-columns: repeat(1, 1fr) !important;
    }

    .no-margin-mobile {
        margin: 0 !important;
    }
}

.title-student {
    max-height: 100px;
}

@media (max-width: 700px) {
    .title-student {
        max-height: 65px;
    }
}

.pack-block {
    position: relative;
    border-radius: 5px;

    .is-size-content {
        font-size: 1.5rem;
    }

    .top-right-img {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;
        max-height: 24%;
    }

    @media (max-width: 700px) {
        .top-right-img {
            max-height: 70px;
        }

        .is-size-content {
            font-size: 1.2rem;
        }
    }
}

.image-header-image {
    align-content: center;

    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }
}
</style>
